import ContextMenu from "devextreme-react/cjs/context-menu";
import ValidationGroup from "devextreme-react/validation-group";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../../hooks/useLanguage";
import { ItiMessageService } from "../../services/iti-message-service";
import ItiButton from "../ItiButton/ItiButton";
import "./ItiForm.scss";
import { calculateTitle } from "./ItiFormUtils";

interface ItiFormProps {
	buttons?: any;
	children?: any;
	title?: string;
	card?: boolean;
	showEditButton?: boolean;
	showBackButton?: boolean;
	showDownloadCsvButton?: boolean;
	showDeleteButton?: boolean;
	showDeletePermanentlyButton?: boolean;
	showRestoreButton?: boolean;
	downloadCsvDisabled?: boolean;
	showPaddingTop?: boolean;
	noForm?: boolean;
	isPopup?: boolean;
	classes?: {
		containerButtons?: string;
		buttons?: string;
		paddingForm?: string;
	};
	showPaddingForm?: boolean;
	idMenuOptions: string;
	paddingClasses?: string;
	rowClass?: string;
	optionsMenuClass?: string;
	backButtonText?: string;
	onEdit?: () => void;
	onDownloadCsv?: () => void;
	onDelete?: () => void;
	onDeletePermanently?: () => void;
	onBack?: () => void;
	onRestore?: () => void;
	onOptionsChange?: (
		options: Array<{ text: string; icon: string; onClick: () => void }>,
	) => void;
}

export default function ItiForm({
	children,
	buttons,
	title,
	card = true,
	showEditButton = false,
	showBackButton = false,
	showRestoreButton = false,
	showDownloadCsvButton = false,
	showDeleteButton = false,
	showDeletePermanentlyButton = false,
	downloadCsvDisabled = false,
	classes,
	noForm = false,
	isPopup = false,
	showPaddingForm = true,
	paddingClasses = "",
	idMenuOptions = "menu-options",
	optionsMenuClass,
	rowClass,
	backButtonText,
	showPaddingTop = true,
	onEdit,
	onRestore,
	onDelete,
	onDeletePermanently,
	onBack,
	onDownloadCsv,
	onOptionsChange,
}: Readonly<ItiFormProps>) {
	const { t } = useLanguage();
	const location = useLocation();
	const [menuVisible, setMenuVisible] = useState(false);

	const classesByDefault = {
		containerButtons: showPaddingTop
			? "button-panel padding-top-10 d-flex justify-content-end"
			: "button-panel d-flex justify-content-end pt-2",
		buttons: `text-end d-flex justify-content-end ${isPopup ? "mt-4" : ""}`,
		paddingForm: getPaddingFormClasses(
			isPopup,
			showPaddingForm,
			paddingClasses,
		),
	};
	const [classesCss] = useState({ ...classesByDefault, ...classes });
	const cardMode = card ? "content-block dx-card responsive-paddings" : "";

	function getPaddingFormClasses(
		isPopup: boolean,
		showPaddingForm: boolean,
		paddingClasses: string,
	) {
		if (paddingClasses) return paddingClasses;
		if (isPopup && !paddingClasses && showPaddingForm)
			return "padding-form-popup";
		if (showPaddingForm) return "padding-form";
		return "";
	}

	const confirmDeletion = () => {
		ItiMessageService.confirmYesNoButtons("messages.question-delete").then(
			(res: boolean) => {
				if (res && onDelete) {
					onDelete();
				}
			},
		);
	};

	const confirmPermanentDeletion = () => {
		ItiMessageService.confirmYesNoButtons(
			"messages.question-delete-permanently",
		).then((res: boolean) => {
			if (res && onDeletePermanently) {
				onDeletePermanently();
			}
		});
	};

	const handleOptionsButtonClick = () => {
		setMenuVisible(!menuVisible);
	};

	const optionsMenuItems = [
		// Botón Restaurar
		{
			text: t("restore"),
			icon: "mdi mdi-restore",
			visible: showRestoreButton,
			onClick: onRestore,
		},
		// Botón Editar
		{
			text: t("edit"),
			icon: "mdi mdi-pencil-outline",
			visible: showEditButton,
			onClick: onEdit,
		},
		// Botón Descargar CSV
		{
			text: t("glossary.export.download-csv"),
			icon: "mdi mdi-download",
			visible: showDownloadCsvButton,
			disabled: downloadCsvDisabled,
			onClick: onDownloadCsv,
		},
		// Botón Eliminar
		{
			text: t("delete"),
			icon: "mdi mdi-trash-can-outline",
			visible: showDeleteButton,
			onClick: confirmDeletion,
		},
		// Botón Eliminar Permanentemente
		{
			text: t("delete-permanently"),
			icon: "mdi mdi-trash-can",
			visible: showDeletePermanentlyButton,
			onClick: confirmPermanentDeletion,
		},
	];

	const filteredOptions = optionsMenuItems.filter((item) => item.visible);
	const showOptionsMenu = filteredOptions.length > 0 && !isPopup;

	useEffect(() => {
		if (isPopup && onOptionsChange) {
			onOptionsChange(filteredOptions as any);
		}
	}, [isPopup, onOptionsChange, filteredOptions, showEditButton]);

	function backTitle() {
		return backButtonText ? t(backButtonText) : t(calculateTitle(location));
	}

	const FormWrapper = noForm ? "div" : "form";

	return (
		<div className="content">
			{showBackButton && (
				<div className="row p-4 pb-0 align-items-center">
					<div className="col-auto">
						<ItiButton
							icon="mdi mdi-chevron-left"
							elementAttr={{ class: "btn-back" }}
							onClick={onBack}
							hint={t("back")}
						/>
					</div>

					<div className="col p-0 back-title">{backTitle()}</div>
				</div>
			)}

			{(title || showOptionsMenu) && (
				<div
					className={
						!optionsMenuClass ? "content-block mt-3" : optionsMenuClass
					}
				>
					<div className="row">
						<div className="col">
							{title && (
								<h2>
									{title.includes("mdi mdi-")
										? (() => {
												const parts = title.split(" ");
												const iconClass = `${parts[0]} ${parts[1]}`;
												const titleText = parts.slice(2).join(" ");
												return (
													<span className="d-flex align-items-center">
														<i
															className={iconClass}
															style={{ fontSize: "20px", marginRight: "10px" }}
														/>
														<span>{titleText}</span>
													</span>
												);
											})()
										: title}
								</h2>
							)}
						</div>
						{showOptionsMenu && (
							<div className="col-auto text-end">
								<ItiButton
									icon="mdi mdi-dots-horizontal"
									elementAttr={{
										class: "m-3 options-title",
										id: idMenuOptions,
									}}
									visible={!location.pathname.includes("recycle-bin")}
									onClick={handleOptionsButtonClick}
								/>
								<ContextMenu
									dataSource={filteredOptions || []}
									width={200}
									target={`#${idMenuOptions}`}
									visible={menuVisible}
									onHiding={() => setMenuVisible(false)}
								/>
							</div>
						)}
					</div>
				</div>
			)}

			<div className={cardMode}>
				<FormWrapper className={classesCss.paddingForm}>
					<ValidationGroup>
						{Array.isArray(children)
							? children.map((element: any, index: number) => {
									return (
										<div className={rowClass ? rowClass : "mb-3"} key={index}>
											{element}
										</div>
									);
								})
							: children}
						<div className={classesCss.containerButtons}>
							<div className={classesCss.buttons}>{buttons}</div>
						</div>
					</ValidationGroup>
				</FormWrapper>
			</div>
		</div>
	);
}
